/**
 * Hero Cards Layout for Poker Reborn
 *
 * Displays the hero's hole cards with prominent styling.
 * Supports variable card count (2 for Hold'em, 4 for Omaha).
 * Medium-sized cards with wider spacing and pronounced fan effect.
 *
 * @see docs/screens/room/CARDS_HERO.md - Visual specs
 * @see docs/screens/room/CARD_VIEW.md - CardView component reference
 */

import {Container, Graphics} from 'pixi.js';
import {CardView, CARD_STYLES} from './CardView';
import type {Card} from '@/types/game';
import {DEALING_CONFIG} from '@/config/dealingAnimation';
import {HERO_CARD_WIDTH} from '@/config/cockpit';
import {getScaledValue} from '@/utils/responsive';

// =============================================================================
// HERO LAYOUT CONFIG
// =============================================================================

const LAYOUT = {
    /** Card size for hero */
    cardSize: 'medium' as const,

    /** Spacing multiplier (relative to card width) — 2-card layout */
    spacingMultiplier: 0.75,

    /** Tighter spacing for 4-card (Omaha) layout — same total spread as 2 cards */
    spacingMultiplier4: 0.30,

    /** Fan rotation range (radians) — cards interpolate from -maxRotation to +maxRotation */
    maxRotation: 0.10, // ~6 degrees
};

// =============================================================================
// BREAKPOINT-BASED SCALING (BBS) — card width lives in cockpit.ts
// =============================================================================

const ANIMATION = {
    /** Deal animation timing */
    deal: {
        duration: 650,
        stagger: 120,
        arcHeight: 80,
    },

    /** Fold animation timing */
    fold: {
        flipDuration: 200,
        slideDuration: 300,
        fadeDuration: 200,
    },

    /** Highlight effect */
    highlight: {
        glowColor: 0xffd700,
        glowAlpha: 0.8,
        pulseFrequency: 2,
        pulseDuration: 1000,
        liftAmount: 12,
    },

    /** Show animation (showdown flourish) */
    show: {
        spreadAmount: 18,     // px each card moves outward
        liftAmount: 15,       // px upward movement
        duration: 300,        // ms
    },
};

// =============================================================================
// EASING FUNCTIONS
// =============================================================================

/** Smooth deceleration - cards slow down gently at the end */
function easeOutPower3(progress: number): number {
    return 1 - Math.pow(1 - progress, 3);
}

function easeInPower2(progress: number): number {
    return progress * progress;
}

// =============================================================================
// HELPER: compute card positions and rotations for N cards
// =============================================================================

function computeCardLayout(cardCount: number, spacing: number): { positions: number[]; rotations: number[] } {
    const positions: number[] = [];
    const rotations: number[] = [];
    for (let i = 0; i < cardCount; i++) {
        positions.push((i - (cardCount - 1) / 2) * spacing);
        const t = cardCount > 1 ? i / (cardCount - 1) : 0.5;
        rotations.push(-LAYOUT.maxRotation + 2 * LAYOUT.maxRotation * t);
    }
    return { positions, rotations };
}

// =============================================================================
// HERO CARDS LAYOUT
// =============================================================================

export class HeroCardsLayout extends Container {
    private cards: CardView[] = [];
    private basePositions: number[] = [];
    private baseRotations: number[] = [];
    private spacing: number;

    // Responsive scaling
    private currentScale = 1;

    // Glow effect
    private glowGraphics: Graphics;
    private isHighlighted = false;
    private pulseAnimationId: number | null = null;

    // Track original Y positions for highlight lift
    private originalCardYs: number[] = [];

    // Backward-compat accessors for batch animation methods
    private get card1(): CardView { return this.cards[0]!; }
    private get card2(): CardView { return this.cards[1]!; }
    private get baseCard1X(): number { return this.basePositions[0]!; }
    private get baseCard2X(): number { return this.basePositions[1]!; }

    constructor(cardCount = 2) {
        super();
        this.label = 'heroCardsLayout';

        const dims = CARD_STYLES[LAYOUT.cardSize];
        const multiplier = cardCount > 2 ? LAYOUT.spacingMultiplier4 : LAYOUT.spacingMultiplier;
        this.spacing = dims.width * multiplier;

        // Create glow graphics (behind cards)
        this.glowGraphics = new Graphics();
        this.glowGraphics.visible = false;
        this.addChild(this.glowGraphics);

        this.buildCards(cardCount);
    }

    // ===========================================================================
    // CARD SLOT MANAGEMENT
    // ===========================================================================

    /**
     * (Re)build card slots for the given count.
     * Removes old CardViews and creates new ones.
     */
    private buildCards(cardCount: number): void {
        // Remove existing cards
        for (const card of this.cards) {
            this.removeChild(card);
        }

        const { positions, rotations } = computeCardLayout(cardCount, this.spacing);
        this.basePositions = positions;
        this.baseRotations = rotations;
        this.originalCardYs = new Array<number>(cardCount).fill(0);
        this.cards = [];

        for (let i = 0; i < cardCount; i++) {
            const card = new CardView(LAYOUT.cardSize);
            card.x = positions[i]!;
            card.rotation = rotations[i]!;
            this.addChild(card);
            this.cards.push(card);
        }
    }

    /**
     * Change the number of card slots. Call before dealing a new hand.
     */
    setCardCount(cardCount: number): void {
        if (this.cards.length === cardCount) return;
        this.buildCards(cardCount);
        // Re-apply current responsive scale
        for (const card of this.cards) {
            card.scale.set(this.currentScale);
        }
        this.recalcSpacing();
    }

    // ===========================================================================
    // PUBLIC METHODS
    // ===========================================================================

    /**
     * Deal cards with animation
     * @param cards - The hole cards
     * @param faceUp - Whether to flip face up after dealing
     * @param fromPosition - Optional start position in parent coordinates (default: above component)
     */
    async deal(cards: Card[], faceUp: boolean, fromPosition?: { x: number; y: number }): Promise<void> {
        for (let i = 0; i < Math.min(cards.length, this.cards.length); i++) {
            this.cards[i]!.setCard(cards[i]!);
        }
        await this.animateDeal(faceUp, fromPosition);
    }

    /**
     * Reveal cards (flip face up)
     */
    async reveal(cards: Card[]): Promise<void> {
        for (let i = 0; i < Math.min(cards.length, this.cards.length); i++) {
            this.cards[i]!.setCard(cards[i]!);
        }

        const promises: Promise<void>[] = [];
        for (let i = 0; i < this.cards.length; i++) {
            const cardView = this.cards[i]!;
            const delay = i * 100;
            promises.push(
                new Promise<void>((resolve) => {
                    setTimeout(async () => {
                        await cardView.flip(true, true);
                        resolve();
                    }, delay);
                })
            );
        }
        await Promise.all(promises);
    }

    /**
     * Show cards at showdown - dramatic flourish animation.
     * Cards spread apart, lift up, and scale up.
     * Used when hero voluntarily shows cards (already face-up).
     */
    async show(): Promise<void> {
        await this.animateShow();
    }

    /**
     * Fold cards - dim but keep visible until hand ends
     */
    async fold(): Promise<void> {
        this.darken(true);
    }

    /**
     * Muck cards - animate cards flying to muck pile.
     * Used when player chooses not to show at showdown.
     */
    async muck(): Promise<void> {
        await this.animateFold();
    }

    /**
     * Set cards without animation
     */
    setCards(cards: Card[], faceUp = true): void {
        for (let i = 0; i < Math.min(cards.length, this.cards.length); i++) {
            this.cards[i]!.setCard(cards[i]!);
            this.cards[i]!.flip(faceUp, false);
        }
    }

    /**
     * Show card backs - no animation
     */
    showBacks(): void {
        // Reset any highlights from previous hand
        this.resetHighlights();

        // Ensure layout is visible
        this.visible = true;

        for (const card of this.cards) {
            card.setCard(null);
            card.flip(false, false);
        }
    }

    /**
     * Deal card backs with animation
     * @param fromPosition - Optional start position in local coordinates (default: above component)
     */
    async dealBacks(fromPosition?: { x: number; y: number }): Promise<void> {
        // Reset any highlights from previous hand
        this.resetHighlights();

        // Ensure layout is visible
        this.visible = true;

        for (const card of this.cards) {
            card.setCard(null);
        }
        await this.animateDeal(false, fromPosition);
    }

    /**
     * Deal a single card with animation (for round-robin dealing)
     * @param cardIndex Index of the card slot (0-based)
     * @param card The card data to display
     * @param fromPosition Starting position (deck location)
     */
    async dealSingleCard(
        cardIndex: number,
        card: Card,
        fromPosition?: { x: number; y: number }
    ): Promise<void> {
        const targetCard = this.cards[cardIndex];
        const targetX = this.basePositions[cardIndex];
        const targetRotation = this.baseRotations[cardIndex];
        if (!targetCard || targetX === undefined || targetRotation === undefined) return;

        // Reset highlights and positions when dealing first card (start of new hand)
        if (cardIndex === 0) {
            this.resetCardPositions();
            this.resetHighlights();
            // Hide all cards except first until they're dealt
            for (let i = 1; i < this.cards.length; i++) {
                this.cards[i]!.alpha = 0;
            }
        }

        // Set card data and start face-up (no flip animation)
        targetCard.setCard(card);
        targetCard.flip(true, false);

        // Animate from start position to final position (card already face-up)
        await this.animateSingleCard(targetCard, targetX, targetRotation, fromPosition);
    }

    /**
     * Highlight cards with glow and pulse effect
     */
    highlight(on: boolean): void {
        if (on === this.isHighlighted) return;
        this.isHighlighted = on;

        if (on) {
            this.drawGlow();
            this.glowGraphics.visible = true;

            for (let i = 0; i < this.cards.length; i++) {
                this.originalCardYs[i] = this.cards[i]!.y;
                this.cards[i]!.y -= ANIMATION.highlight.liftAmount;
            }

            this.startPulseAnimation();
        } else {
            this.stopPulseAnimation();
            this.glowGraphics.visible = false;

            for (let i = 0; i < this.cards.length; i++) {
                this.cards[i]!.y = this.originalCardYs[i] ?? 0;
            }
        }
    }

    /**
     * Highlight specific winning cards.
     * Core cards get strong gold highlight, kicker cards get subtle gray highlight.
     */
    highlightWinningCards(coreCards: Card[], kickerCards: Card[] = []): void {
        for (const cardView of this.cards) {
            const cardData = cardView.getCard();

            const isCore =
                cardData &&
                coreCards.some((wc) => wc.rank === cardData.rank && wc.suit === cardData.suit);
            const isKicker =
                cardData &&
                kickerCards.some((wc) => wc.rank === cardData.rank && wc.suit === cardData.suit);

            if (isCore) {
                cardView.highlight(true, 'strong');
                cardView.darken(false);
            } else if (isKicker) {
                cardView.highlight(true, 'subtle');
                cardView.darken(false);
            } else {
                cardView.highlight(false);
                cardView.darken(true);
            }
        }
    }

    /**
     * Reset highlights
     */
    resetHighlights(): void {
        for (const card of this.cards) {
            card.highlight(false);
            card.darken(false);
        }
        this.highlight(false);
    }

    /**
     * Darken cards
     */
    darken(on: boolean): void {
        for (const card of this.cards) {
            card.darken(on);
        }
    }

    /**
     * Get current card data (for state transfer)
     */
    getState(): { card1: Card | null; card2: Card | null; card1FaceUp: boolean; card2FaceUp: boolean } {
        return {
            card1: this.cards[0]?.getCard() ?? null,
            card2: this.cards[1]?.getCard() ?? null,
            card1FaceUp: this.cards[0]?.isFaceUpState() ?? false,
            card2FaceUp: this.cards[1]?.isFaceUpState() ?? false,
        };
    }

    /**
     * Restore state (for state transfer)
     */
    restoreState(state: { card1: Card | null; card2: Card | null; card1FaceUp: boolean; card2FaceUp: boolean }): void {
        if (state.card1 && this.cards[0]) this.cards[0].setCard(state.card1);
        if (state.card2 && this.cards[1]) this.cards[1].setCard(state.card2);
        this.cards[0]?.flip(state.card1FaceUp, false);
        this.cards[1]?.flip(state.card2FaceUp, false);
    }

    /**
     * Reset to initial state
     */
    reset(): void {
        this.resetCardPositions();
        this.resetHighlights();
        for (const card of this.cards) {
            card.setCard(null);
            card.flip(false, false);
        }
    }

    /**
     * Force redraw of card graphics after GPU context loss.
     * @see docs/systems/BACKGROUND_TAB_RECOVERY.md
     */
    redraw(): void {
        for (const card of this.cards) {
            card.redraw();
        }
    }

    /**
     * Nuclear reset - restore ALL card properties to pristine state.
     * Called at hand start to ensure clean slate.
     */
    resetAllCards(): void {
        // Stop any highlight animations
        this.stopPulseAnimation();
        this.isHighlighted = false;
        this.glowGraphics.visible = false;
        this.glowGraphics.clear();

        // Reset original position tracking
        this.originalCardYs = new Array<number>(this.cards.length).fill(0);

        // Nuclear reset on each card (resets all visual properties)
        for (let i = 0; i < this.cards.length; i++) {
            const card = this.cards[i]!;
            card.resetAll();
            card.scale.set(this.currentScale);
            card.x = this.basePositions[i]!;
            card.y = 0;
            card.rotation = this.baseRotations[i]!;
        }
    }

    /**
     * Update layout for screen size (responsive scaling)
     * @param screenWidth - Current screen width in pixels
     */
    updateLayout(screenWidth: number): void {
        const cardWidth = getScaledValue(screenWidth, HERO_CARD_WIDTH, { discrete: false });
        const baseWidth = CARD_STYLES[LAYOUT.cardSize].width;
        const scale = cardWidth / baseWidth;
        this.currentScale = scale;

        this.recalcSpacing();

        // Apply scale and update positions
        for (let i = 0; i < this.cards.length; i++) {
            this.cards[i]!.scale.set(scale);
            this.cards[i]!.x = this.basePositions[i]!;
        }

        // Redraw glow if highlighted
        if (this.isHighlighted) {
            this.drawGlow();
        }
    }

    // ===========================================================================
    // PRIVATE - SPACING RECALCULATION
    // ===========================================================================

    private recalcSpacing(): void {
        const dims = CARD_STYLES[LAYOUT.cardSize];
        const multiplier = this.cards.length > 2 ? LAYOUT.spacingMultiplier4 : LAYOUT.spacingMultiplier;
        this.spacing = dims.width * multiplier * this.currentScale;
        const { positions, rotations } = computeCardLayout(this.cards.length, this.spacing);
        this.basePositions = positions;
        this.baseRotations = rotations;
    }

    // ===========================================================================
    // PRIVATE - DEAL ANIMATION
    // ===========================================================================

    private animateDeal(faceUp: boolean, fromPosition?: { x: number; y: number }): Promise<void> {
        return new Promise((resolve) => {
            const {duration, stagger, arcHeight} = ANIMATION.deal;

            // Use provided start position or default above component
            const startX = fromPosition?.x ?? 0;
            const startY = fromPosition?.y ?? -250;
            const targetY = 0;

            for (const card of this.cards) {
                card.y = startY;
                card.x = startX;
                card.alpha = 0;
                card.scale.set(0.5 * this.currentScale);
                card.flip(true, false);
            }

            const startTime = Date.now();
            const totalDuration = duration + stagger * (this.cards.length - 1);

            const animate = (): void => {
                const elapsed = Date.now() - startTime;

                for (let i = 0; i < this.cards.length; i++) {
                    const card = this.cards[i]!;
                    const cardElapsed = Math.max(0, elapsed - i * stagger);
                    const progress = Math.min(cardElapsed / duration, 1);
                    const eased = easeOutPower3(progress);
                    const arcOffset = Math.sin(progress * Math.PI) * arcHeight;

                    card.y = startY + (targetY - startY) * eased - arcOffset;
                    card.x = startX + (this.basePositions[i]! - startX) * eased;
                    card.alpha = Math.min(progress * 3, 1);
                    card.scale.set((0.5 + 0.5 * eased) * this.currentScale);
                }

                if (elapsed >= totalDuration) {
                    for (let i = 0; i < this.cards.length; i++) {
                        this.cards[i]!.y = targetY;
                        this.cards[i]!.x = this.basePositions[i]!;
                    }
                    resolve();
                } else {
                    requestAnimationFrame(animate);
                }
            };

            requestAnimationFrame(animate);
        });
    }

    /**
     * Animate a single card from start position to target (for round-robin dealing)
     */
    private animateSingleCard(
        cardView: CardView,
        targetX: number,
        targetRotation: number,
        fromPosition?: { x: number; y: number }
    ): Promise<void> {
        return new Promise((resolve) => {
            const { cardFlightDuration: duration, arcHeight } = DEALING_CONFIG;

            const startX = fromPosition?.x ?? 0;
            const startY = fromPosition?.y ?? -150;
            const targetY = 0;

            cardView.x = startX;
            cardView.y = startY;
            cardView.alpha = 0;
            cardView.rotation = 0;
            cardView.scale.set(0.6 * this.currentScale);
            cardView.visible = true;

            const startTime = Date.now();

            const animate = (): void => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const eased = easeOutPower3(progress);
                const arcOffset = Math.sin(progress * Math.PI) * arcHeight;

                cardView.x = startX + (targetX - startX) * eased;
                cardView.y = startY + (targetY - startY) * eased - arcOffset;
                cardView.alpha = Math.min(progress * 2, 1);
                cardView.rotation = targetRotation * eased;
                cardView.scale.set((0.6 + 0.4 * eased) * this.currentScale);

                if (progress >= 1) {
                    // Ensure exact final position/rotation (alpha/scale already correct)
                    cardView.x = targetX;
                    cardView.y = targetY;
                    cardView.rotation = targetRotation;
                    resolve();
                } else {
                    requestAnimationFrame(animate);
                }
            };

            requestAnimationFrame(animate);
        });
    }

    // ===========================================================================
    // PRIVATE - SHOW ANIMATION
    // ===========================================================================

    /**
     * Animate show flourish: spread apart, lift up, straighten
     */
    private animateShow(): Promise<void> {
        return new Promise((resolve) => {
            const { spreadAmount, liftAmount, duration } = ANIMATION.show;
            const startTime = Date.now();

            // Capture starting values
            const startXs = this.cards.map((c) => c.x);
            const startY = this.cards[0]?.y ?? 0;
            const startRotations = this.cards.map((c) => c.rotation);

            // Target: spread wider, lift, straighten
            const targetXs = this.basePositions.map((pos, i) => {
                const direction = i < this.cards.length / 2 ? -1 : 1;
                return pos + direction * spreadAmount;
            });
            const targetY = -liftAmount;

            const animate = (): void => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const eased = easeOutPower3(progress);

                for (let i = 0; i < this.cards.length; i++) {
                    const card = this.cards[i]!;
                    card.x = startXs[i]! + (targetXs[i]! - startXs[i]!) * eased;
                    card.y = startY + (targetY - startY) * eased;
                    card.rotation = startRotations[i]! * (1 - eased);
                }

                if (progress >= 1) {
                    for (let i = 0; i < this.cards.length; i++) {
                        this.cards[i]!.x = targetXs[i]!;
                        this.cards[i]!.y = targetY;
                        this.cards[i]!.rotation = 0;
                    }
                    resolve();
                } else {
                    requestAnimationFrame(animate);
                }
            };

            requestAnimationFrame(animate);
        });
    }

    // ===========================================================================
    // PRIVATE - FOLD ANIMATION
    // ===========================================================================

    private async animateFold(): Promise<void> {
        const {slideDuration, fadeDuration} = ANIMATION.fold;

        // Clear any darkening before animating
        for (const card of this.cards) {
            card.darken(false);
        }

        await Promise.all(this.cards.map((card) => card.flip(false, true)));

        await this.delay(50);
        await this.animateSlideToMuck(slideDuration);
        await this.animateFadeOut(fadeDuration);
        // Hide layout BEFORE resetting positions to prevent flash
        this.visible = false;
        this.resetCardPositions();
    }

    private animateSlideToMuck(duration: number): Promise<void> {
        return new Promise((resolve) => {
            const startYs = this.cards.map((c) => c.y);
            const startXs = this.cards.map((c) => c.x);
            const targetY = -120;
            const targetX = 0;

            const startTime = Date.now();

            const animate = (): void => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const eased = easeInPower2(progress);

                for (let i = 0; i < this.cards.length; i++) {
                    const card = this.cards[i]!;
                    card.y = startYs[i]! + (targetY - startYs[i]!) * eased;
                    card.x = startXs[i]! + (targetX - startXs[i]!) * eased;
                    card.rotation = this.baseRotations[i]! * (1 - eased);
                }

                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    resolve();
                }
            };

            requestAnimationFrame(animate);
        });
    }

    private animateFadeOut(duration: number): Promise<void> {
        return new Promise((resolve) => {
            const startAlpha = this.cards[0]?.alpha ?? 1;
            const startTime = Date.now();

            const animate = (): void => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);

                const newAlpha = startAlpha * (1 - progress);
                for (const card of this.cards) {
                    card.alpha = newAlpha;
                }

                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    resolve();
                }
            };

            requestAnimationFrame(animate);
        });
    }

    private resetCardPositions(): void {
        for (let i = 0; i < this.cards.length; i++) {
            const card = this.cards[i]!;
            card.y = 0;
            card.x = this.basePositions[i]!;
            card.alpha = 1;
            card.rotation = this.baseRotations[i]!;
            card.scale.set(this.currentScale);
        }
    }

    // ===========================================================================
    // PRIVATE - HIGHLIGHT/GLOW
    // ===========================================================================

    private drawGlow(): void {
        const dims = CARD_STYLES[LAYOUT.cardSize];
        const {glowColor} = ANIMATION.highlight;

        this.glowGraphics.clear();

        const cardCount = this.cards.length;
        const totalSpread = cardCount > 1 ? (cardCount - 1) * this.spacing : 0;
        const glowPadding = 6;
        const totalWidth = dims.width + totalSpread + glowPadding * 2;
        const totalHeight = dims.height + glowPadding * 2;

        for (let i = 3; i >= 0; i--) {
            const expand = i * 3;
            const alpha = 0.2 * (4 - i);
            this.glowGraphics.roundRect(
                -totalWidth / 2 - expand,
                -totalHeight / 2 - expand - ANIMATION.highlight.liftAmount,
                totalWidth + expand * 2,
                totalHeight + expand * 2,
                8 + i
            );
            this.glowGraphics.fill({color: glowColor, alpha});
        }
    }

    private startPulseAnimation(): void {
        const {pulseFrequency, pulseDuration} = ANIMATION.highlight;
        const startTime = Date.now();

        const pulse = (): void => {
            if (!this.isHighlighted) return;

            const elapsed = Date.now() - startTime;
            const cycleTime = 1000 / pulseFrequency;
            const cycleProgress = (elapsed % cycleTime) / cycleTime;

            const pulseValue = 0.6 + 0.4 * Math.sin(cycleProgress * Math.PI * 2);
            this.glowGraphics.alpha = pulseValue;

            if (elapsed < pulseDuration || this.isHighlighted) {
                this.pulseAnimationId = requestAnimationFrame(pulse);
            }
        };

        pulse();
    }

    private stopPulseAnimation(): void {
        if (this.pulseAnimationId !== null) {
            cancelAnimationFrame(this.pulseAnimationId);
            this.pulseAnimationId = null;
        }
        this.glowGraphics.alpha = 1;
    }

    // ===========================================================================
    // PRIVATE - UTILITIES
    // ===========================================================================

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}
